import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { OtpInput } from '@/components/ui/otp-input';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { useAuthStore } from '@/stores/auth-store';

/**
 * Step-up confirmation for actions that need an elevated session. Always confirms
 * the account password; when two-factor is on, also requires an authenticator code.
 */

const TOTP_ENROLLMENT_CODES = new Set(['TOTP_NOT_ENABLED', 'TOTP_ENROLLMENT_REQUIRED']);

export interface ElevateSessionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Plain-language reason for this confirmation. Defaults to a generic prompt. */
  description?: string;
  /** Called after the session is elevated successfully. */
  onElevated: () => void;
  onTotpEnrollmentRequired?: () => void;
}

export const ElevateSessionDialog = ({
  open,
  onOpenChange,
  description,
  onElevated,
  onTotpEnrollmentRequired,
}: ElevateSessionDialogProps) => {
  const { t } = useTranslation('security');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const user = useAuthStore((state) => state.user);
  const isTotpEnabled = user?.isTotpEnabled ?? false;
  const hasPassword = user?.hasPassword ?? false;

  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [wasOpen, setWasOpen] = useState(open);

  if (open !== wasOpen) {
    setWasOpen(open);
    if (!open) {
      setCode('');
      setPassword('');
    }
  }

  const elevate = useMutation({
    mutationFn: (input: { password: string; code?: string }) =>
      api.post<{ elevated: true }>('/auth/session/elevate', input, { withoutChurch: true }),
    onSuccess: () => {
      setCode('');
      setPassword('');
      onElevated();
    },
    onError: (err) => {
      setCode('');
      if (err instanceof ApiError && TOTP_ENROLLMENT_CODES.has(err.code)) {
        onTotpEnrollmentRequired?.();
        return;
      }
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const canSubmitPassword = password.trim().length > 0;
  const canSubmitTotp = !isTotpEnabled || code.length === 6;
  const canSubmit = canSubmitPassword && canSubmitTotp;
  const needsPasswordSetup = !hasPassword;

  const handleSubmit = () => {
    if (!canSubmit || elevate.isPending) return;
    elevate.mutate({
      password,
      code: isTotpEnabled ? code : undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md" closeLabel={tCommon('action.close')} className="overflow-visible">
        <DialogHeader>
          <DialogTitle>{t('elevate.passwordTitle')}</DialogTitle>
          <DialogDescription>{description ?? t('elevate.description')}</DialogDescription>
        </DialogHeader>

        {needsPasswordSetup ? (
          <div className="space-y-3 text-body-sm text-fg-secondary">
            <p>{t('elevate.noPasswordDescription')}</p>
            <Link to="/account" className="text-brand underline-offset-2 hover:underline">
              {t('elevate.setPasswordLink')}
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="elevate-password">{t('elevate.password')}</Label>
              <Input
                id="elevate-password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                autoFocus
                disabled={elevate.isPending}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !isTotpEnabled) handleSubmit();
                }}
              />
              <p className="text-caption text-fg-secondary">{t('elevate.passwordHint')}</p>
            </div>

            {isTotpEnabled ? (
              <div className="space-y-2">
                <Label id="elevate-totp-label" className="text-center block">
                  {t('elevate.code')}
                </Label>
                <OtpInput
                  value={code}
                  onChange={setCode}
                  disabled={elevate.isPending}
                  aria-labelledby="elevate-totp-label"
                />
                <p className="text-center text-caption text-fg-secondary">{t('elevate.codeHint')}</p>
              </div>
            ) : null}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={elevate.isPending}
          >
            {tCommon('action.cancel')}
          </Button>
          {needsPasswordSetup ? (
            <Button asChild>
              <Link to="/account">{t('elevate.setPasswordLink')}</Link>
            </Button>
          ) : (
            <Button disabled={!canSubmit} loading={elevate.isPending} onClick={handleSubmit}>
              {t('elevate.confirm')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
